import Button from '@/common/components/base/button/Button';
import LoadingButton from '@/common/components/base/button/loadingButton';
import DateInput from '@/common/components/base/input/dateInput/DateInput';
import ModalInput from '@/common/components/base/input/modalInput/ModalInput';
import TextInput from '@/common/components/base/input/textInput/TextInput';
import { PERSIAN_LATTER_RULE } from '@/common/components/base/input/textInput/textInput.rules';
import { useCountryRequest } from '@/common/hooks/useCountryRequest';
import useDebounce from '@/common/hooks/useDebounce';
import { UseGetAllCounty } from '@/common/hooks/UseGetAllCounty';
import { useLicenseTypeRequest } from '@/common/hooks/useLicenseTypeRequest';
import { convertPersianToGregorian } from '@/common/utils/convertPersianToGregorian';
import { DRIVING_LICENSE_REGEX } from '@/common/utils/rules/drivingLicenseRegex.rules';
import { requiredText } from '@/common/utils/rules/publicRules';
import { ADJUSTER_REPORT_CULPRIT_LINKS } from '@/modules/adjusterReportModules/adjusterReport/links/culprit/culpritLinks';
import { validateNotFutureDate } from '@/modules/adjusterReportModules/IncidentDetails/utils/incidentDetails.util';
import { GetCulpritDriverInfoFormType } from '@/modules/adjusterReportModules/IncidentParticipants/queries/culprit/getculpritDriverInfo/getCulpritDriverInfoQuery.type';
import { getCulpritDriverInfoQueryOptions } from '@/modules/adjusterReportModules/IncidentParticipants/queries/culprit/getculpritDriverInfo/getCulpritDriverInfoQueryOptions';
import { CulpritCertificationData } from '@/modules/adjusterReportModules/IncidentParticipants/queries/culprit/patchCulpritCertification/patchCulpritCertificationQuery.type';
import { usePatchCulpritCertificationQueryOptions } from '@/modules/adjusterReportModules/IncidentParticipants/queries/culprit/patchCulpritCertification/patchCulpritCertificationQueryOptions';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Flex } from 'styled-system/jsx';
import { LicenseType } from '../../../pages/damaged/propertyDamage/driver-owner/driverInfo/driverInfo.type';

const CulpritCertificateInformation: React.FC = () => {
  const methods = useFormContext<GetCulpritDriverInfoFormType>();
  const [countryName, setCountryName] = React.useState<string>('');
  const [cityName, setCityName] = React.useState<string>('');
  const navigate = useNavigate();
  const debounce = useDebounce();
  const { id } = useParams();
  const selectedCountry = methods.watch(
    'certificate_information.license_issue_location_country',
  );

  const hasCertificate = methods.watch('certificate_information.license_type');
  const isNoLicense = hasCertificate?.value === 13;
  const { licenseTypeQuery } = useLicenseTypeRequest();
  const { mutate: patchCertification, isPending: loading } =
    usePatchCulpritCertificationQueryOptions();
  const { countryQuery } = useCountryRequest(countryName);
  const { countryQuery: defaultCountryQuery } = useCountryRequest('ایران');
  const client = useQueryClient();

  const handleLicenseTypeChange = (option: LicenseType) => {
    if (option.value !== 13) return;

    methods.setValue('certificate_information.license_number', '');

    methods.setValue('certificate_information.license_issue_date', null);

    methods.setValue(
      'certificate_information.license_issue_location_country',
      null,
    );

    methods.setValue(
      'certificate_information.iran_license_issue_location_city',
      null,
    );

    methods.setValue('certificate_information.license_issue_location_city', '');
  };

  useEffect(() => {
    const { certificate_information } = methods.getValues();
    if (!certificate_information?.license_issue_location_country) {
      methods.setValue(
        'certificate_information.license_issue_location_country',
        defaultCountryQuery?.data?.[0] || null,
      );
    }
  }, [defaultCountryQuery]);
  const { data: countyList } = useQuery(UseGetAllCounty(cityName));

  const searchCity = (value: string) =>
    debounce(() => {
      setCityName(value);
    }, 1000);
  const searchCountry = (value: string) =>
    debounce(() => {
      setCountryName(value);
    }, 1000);

  const onSubmit = (formData: GetCulpritDriverInfoFormType) => {
    const { certificate_information } = formData;
    const isNoLicense = hasCertificate?.value === 13;
    const baseData: CulpritCertificationData = {
      license_type: +certificate_information?.license_type?.value || 0,
      license_issue_date: null,
      license_number: null,
      license_issue_location_country: null,
      license_issue_location_city: null,
      iran_license_issue_location_city: null,
    };

    const certificateData: CulpritCertificationData = isNoLicense
      ? baseData
      : {
          ...baseData,
          license_issue_date: convertPersianToGregorian(
            certificate_information?.license_issue_date?.toString() || '',
          ),
          license_number: certificate_information?.license_number || null,
          license_issue_location_country:
            certificate_information?.license_issue_location_country?.value || 0,
          license_issue_location_city:
            certificate_information?.license_issue_location_city || null,
          iran_license_issue_location_city:
            selectedCountry?.value === defaultCountryQuery.data?.[0]?.value
              ? certificate_information?.iran_license_issue_location_city
                  ?.value || null
              : null,
        };

    patchCertification(
      {
        data: certificateData,
        id: +id!,
      },
      {
        onSuccess: () => {
          navigate(
            ADJUSTER_REPORT_CULPRIT_LINKS.CULPRIT_INFORMATION({
              id: +id!,
            }).DRIVER_OWNER_INFORMATION.driverContact,
            { replace: true },
          );
        },
      },
    );
  };
  const previousStep = () => {
    methods.clearErrors();
    methods.trigger();
    client.invalidateQueries({
      queryKey: getCulpritDriverInfoQueryOptions({ id: +id! }).queryKey,
    });

    navigate(
      ADJUSTER_REPORT_CULPRIT_LINKS.CULPRIT_INFORMATION({
        id: +id!,
      }).DRIVER_OWNER_INFORMATION.driver,
      { replace: true },
    );
  };
  return (
    <Flex direction="column" gap={8}>
      <Box>
        <ModalInput
          name="certificate_information.license_type"
          label="نوع گواهی نامه"
          placeholder="انتخاب کنید"
          rules={{ required: requiredText }}
          options={licenseTypeQuery.data || []}
          defaultValue=""
          onChange={handleLicenseTypeChange}
        />
      </Box>
      <Box>
        <TextInput
          label="شماره گواهینامه"
          name="certificate_information.license_number"
          placeholder="شماره گواهینامه را وارد کنید"
          rules={{
            required: isNoLicense ? false : requiredText,
            ...(isNoLicense ? {} : DRIVING_LICENSE_REGEX),
          }}
          defaultValue=""
          noCharInput
          max={10}
          disabled={isNoLicense}
        />
      </Box>
      {!isNoLicense && (
        <Box>
          <DateInput
            label="تاریخ صدور گواهینامه"
            name="certificate_information.license_issue_date"
            buttonText="انتخاب تاریخ"
            modalLabel="انتخاب تاریخ صدور گواهینامه"
            defaultValue=""
            disabled={isNoLicense}
            rules={{
              required: isNoLicense ? false : requiredText,
              validate: validateNotFutureDate,
            }}
            maxDate={new Date()}
          />
        </Box>
      )}
      <Box>
        <ModalInput
          name="certificate_information.license_issue_location_country"
          label="کشور محل صدور گواهینامه"
          placeholder="انتخاب کنید"
          options={countryQuery.data || []}
          defaultValue={defaultCountryQuery.data?.[0]}
          disabled={isNoLicense}
          searchOptions={{
            onChange: searchCountry,
            placeholder: 'جستجو کشور',
          }}
          rules={{ required: isNoLicense ? false : requiredText }}
        />
      </Box>
      {selectedCountry?.value === defaultCountryQuery.data?.[0]?.value && (
        <Box>
          <ModalInput
            name="certificate_information.iran_license_issue_location_city"
            label="شهر محل صدور گواهینامه"
            placeholder="انتخاب کنید"
            options={countyList || []}
            defaultValue=""
            disabled={isNoLicense}
            searchOptions={{
              onChange: searchCity,
              placeholder: 'جستجو شهر',
            }}
            rules={{ required: isNoLicense ? false : requiredText }}
          />
        </Box>
      )}
      {selectedCountry?.value !== defaultCountryQuery.data?.[0]?.value && (
        <Box>
          <TextInput
            label="شهر محل صدور گواهینامه"
            name={
              selectedCountry?.value === defaultCountryQuery.data?.[0]?.value
                ? 'certificate_information.iran_license_issue_location_city'
                : 'certificate_information.license_issue_location_city'
            }
            placeholder="شهر محل صدور گواهینامه را وارد کنید"
            rules={{
              required: isNoLicense ? false : requiredText,
              ...PERSIAN_LATTER_RULE,
            }}
            defaultValue=""
            disabled={isNoLicense}
          />
        </Box>
      )}
      <Flex w="100%" gap={16} mb="1rem">
        <Button fullWidth variant="outlined" onClick={previousStep}>
          مرحله قبل
        </Button>{' '}
        <LoadingButton
          onClick={methods.handleSubmit(onSubmit)}
          loading={loading}
          fullWidth
          disabled={!methods?.formState?.isValid}
        >
          ثبت و ادامه
        </LoadingButton>
      </Flex>
    </Flex>
  );
};
export default CulpritCertificateInformation;
